import React, { useState } from 'react';
import '../styles/ComprehensionUpload.scss';

function ComprehensionUpload({ API_BASE_URL, authToken, userData, onBack }) {
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [questions, setQuestions] = useState([{ text: '', points: 10 }]);
  const [isPublic, setIsPublic] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleAddQuestion = () => {
    setQuestions([...questions, { text: '', points: 10 }]);
  };

  const handleQuestionChange = (index, field, value) => {
    const newQuestions = [...questions];
    newQuestions[index][field] = value;
    setQuestions(newQuestions);
  };

  const handleRemoveQuestion = (index) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await fetch(`${API_BASE_URL}/api/comprehensions`, {
        method: 'POST',
        headers: {
          'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title,
          text,
          questions,
          isPublic,
          teacherId: userData.id
        })
      });

      let errorData;
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        errorData = await response.json();
      } else {
        const textError = await response.text();
        throw new Error(`Server returned non-JSON response: ${textError.substring(0, 100)}...`);
      }

      if (!response.ok) {
        throw new Error(errorData.message || errorData.error || 'Failed to save comprehension');
      }

      setSuccess(true);
      // Reset form
      setTitle('');
      setText('');
      setQuestions([{ text: '', points: 10 }]);
      setIsPublic(false);
    } catch (err) {
      console.error('Error saving comprehension:', err);
      setError(err.message || 'An unexpected error occurred while saving the comprehension');
    } finally {
      setIsLoading(false);
    }
  };

  // Only show this component for teachers
  if (userData?.role !== 'teacher') {
    return (
      <div className="comprehension-upload error-message">
        This feature is only available for teachers.
      </div>
    );
  }

  return (
    <div className="comprehension-upload">
      <button 
        className="back-button"
        onClick={onBack}
      >
        ← Back
      </button>
      
      <h2>Create New Comprehension</h2>
      
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">Comprehension saved successfully!</div>}
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="title">Title:</label>
          <input
            type="text"
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            placeholder="Enter a title for this comprehension"
          />
        </div>

        <div className="form-group">
          <label htmlFor="text">Text:</label>
          <textarea
            id="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            required
            placeholder="Enter the text for students to read"
            rows={10}
          />
        </div>

        <div className="questions-section">
          <h3>Questions</h3>
          {questions.map((question, index) => (
            <div key={index} className="question-group">
              <div className="question-header">
                <h4>Question {index + 1}</h4>
                {questions.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveQuestion(index)}
                    className="remove-question"
                  >
                    Remove
                  </button>
                )}
              </div>
              <textarea
                value={question.text}
                onChange={(e) => handleQuestionChange(index, 'text', e.target.value)}
                placeholder="Enter question text"
                required
              />
              <div className="points-input">
                <label>Points:</label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={question.points}
                  onChange={(e) => handleQuestionChange(index, 'points', parseInt(e.target.value))}
                  required
                />
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={handleAddQuestion}
            className="add-question"
          >
            Add Question
          </button>
        </div>

        {/* Only show the "isPublic" checkbox if the user is 'cillian' */}
        {userData?.username === 'cillian' && (
          <div className="form-group checkbox">
            <label>
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
              />
              Make this comprehension visible to all students
            </label>
          </div>
        )}

        <button
          type="submit"
          className="submit-button"
          disabled={isLoading}
        >
          {isLoading ? 'Saving...' : 'Save Comprehension'}
        </button>
      </form>
    </div>
  );
}

export default ComprehensionUpload; 